'use client';

import { createContext, ReactNode, useContext, useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { CustomerSource } from '@/data';

interface PxPayPlusMember {
  mmid: string;
  phone: string;
  email: string;
}

export interface PxPayPlusParams {
  sign?: string;
  nonce?: string;
  mmid?: string;
  [key: string]: string | undefined;
}

export interface PxPayPlusContextValue {
  isPxPayPlus: boolean;
  isLoading: boolean;
  params: PxPayPlusParams;
  mmid: string;
  phone: string;
  email: string;
  setMmid: (mmid: string) => void;
  setPhone: (phone: string) => void;
  setEmail: (email: string) => void;
}

const PXPAYPLUS_MEMBER_KEY = 'pxpayplus_member';

const PxPayPlusContext = createContext<PxPayPlusContextValue | null>(null);

interface PxPayPlusProviderProps {
  utmSource: string | undefined;
  children: ReactNode;
}

export function PxPayPlusProvider({ utmSource, children }: PxPayPlusProviderProps) {
  const searchParams = useSearchParams();
  const [isPxPayPlus, setIsPxPayPlus] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [params, setParams] = useState<PxPayPlusParams>({});
  const [pxPayPlusMember, setPxPayPlusMemberState] = useState<PxPayPlusMember>({
    mmid: '',
    phone: '',
    email: '',
  });

  // 設置 mmid, phone, email 並保存到 localStorage
  const setPxPayPlusMember = useCallback(
    (key: string, value: string) => {
      const newState = { ...pxPayPlusMember, [key]: value };
      setPxPayPlusMemberState(newState);
      if (typeof window !== 'undefined' && value) {
        localStorage.setItem(PXPAYPLUS_MEMBER_KEY, JSON.stringify(newState));
      }
    },
    [pxPayPlusMember],
  );
  const setMmid = (newValue: string) => {
    setPxPayPlusMember('mmid', newValue);
  };
  const setPhone = (newValue: string) => {
    setPxPayPlusMember('phone', newValue);
  };
  const setEmail = (newValue: string) => {
    setPxPayPlusMember('email', newValue);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // 判斷是否在 PxPayPlus In-App WebView 環境
      setIsPxPayPlus(utmSource === CustomerSource.PXPAYPLUS);

      // 提取 URL 參數
      const urlParams: PxPayPlusParams = {};
      searchParams.forEach((value, key) => {
        urlParams[key] = value;
      });
      setParams(urlParams);

      // 從 URL 或 localStorage 取得 mmid, phone, email
      const urlMmid = searchParams.get('mmid');
      const urlPhone = searchParams.get('phone');
      const urlEmail = searchParams.get('email');
      const storedMemberJson = localStorage.getItem(PXPAYPLUS_MEMBER_KEY);
      const newState = storedMemberJson
        ? (JSON.parse(storedMemberJson) as PxPayPlusMember)
        : {
            mmid: '',
            phone: '',
            email: '',
          };

      if (urlMmid) {
        newState.mmid = urlMmid;
      }
      if (urlPhone) {
        newState.phone = urlPhone;
      }
      if (urlEmail) {
        newState.email = urlEmail;
      }

      if (urlMmid || urlPhone || urlEmail) {
        // url -> state & storage
        setPxPayPlusMemberState(newState);
        localStorage.setItem(PXPAYPLUS_MEMBER_KEY, JSON.stringify(newState));
      } else if (storedMemberJson) {
        // storage -> state
        setPxPayPlusMemberState(newState);
      }

      setIsLoading(false);
    }
  }, [searchParams, utmSource]);

  return (
    <PxPayPlusContext.Provider
      value={{
        isPxPayPlus,
        isLoading,
        params,
        ...pxPayPlusMember,
        setMmid,
        setPhone,
        setEmail,
      }}
    >
      {children}
    </PxPayPlusContext.Provider>
  );
}

export function usePxPayPlusContext(): PxPayPlusContextValue {
  const context = useContext(PxPayPlusContext);

  if (!context) {
    throw new Error('usePxPayPlusContext must be used within PxPayPlusProvider');
  }

  return context;
}

/**
 * Safe version that returns default values if not within provider
 */
export function usePxPayPlusSafe(): PxPayPlusContextValue {
  const context = useContext(PxPayPlusContext);

  if (!context) {
    return {
      isPxPayPlus: false,
      isLoading: true,
      params: {},
      mmid: '',
      phone: '',
      email: '',
      setMmid: () => {},
      setPhone: () => {},
      setEmail: () => {},
    };
  }

  return context;
}
